import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useMutation } from '@tanstack/react-query';
import type { Chat } from '@trinity/chat';
import { useIsFullPageChat } from '@trinity/chat-components/components/Chat/hooks/useIsFullPageChat';
import Toast from '@trinity/chat-components/components/Toast';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { type BaseModalProps, useModal } from '@trinity/chat-components/hooks/useModal';
import { PrimaryButton } from '../presentation/Button.Primary';
import { SecondaryButton } from '../presentation/Button.Secondary';
import { ModalShareChatPicker } from './Modal.ShareChat.Picker';

type Props = {
  chatInstance: Pick<Chat.Instance, 'id' | 'name'>;
} & BaseModalProps;

export const ShareChatModal = ({ chatInstance, open, onClose }: Props) => {
  const [selected, setSelected] = useState<Chat.User[]>([]);

  const isFullPageChat = useIsFullPageChat();

  const apiClient = useChatApiClient();

  const shareMutation = useMutation<unknown, unknown, { chatInstanceId: number; userIdentifiers: string[] }>({
    mutationFn: ({ chatInstanceId, userIdentifiers }) => {
      return apiClient.chatInstances.shareChatInstance({
        chatInstanceId,
        standaloneLink: isFullPageChat,
        userIdentifiers,
      });
    },
    onError: () => {
      Toast.error({
        title: `We ran into an error trying to share this chat!`,
      });
    },
    onSuccess: () => {
      Toast.success({
        title: `Chat shared!`,
      });
      onClose();
    },
  });

  const handleSend = useCallback(() => {
    return shareMutation.mutateAsync({
      chatInstanceId: chatInstance.id,
      userIdentifiers: selected.map(u => u.identifier),
    });
  }, [chatInstance, selected, shareMutation]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleAddUser = useCallback((user: Chat.User) => {
    setSelected(prev => prev.some(u => u.identifier === user.identifier) ? prev : [...prev, user]);
  }, []);

  const handleRemoveUser = useCallback((user: Chat.User) => {
    setSelected(prev => prev.filter(u => u.identifier !== user.identifier));
  }, []);

  const enables = {
    send: !!selected.length && !shareMutation.isPending,
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      onClose={onClose}
      open={open}>

      <Title>Share Chat Thread</Title>

      <DialogContent>
        <Box sx={{ minHeight: '375px' }}>
          <ModalShareChatPicker
            chatInstanceId={chatInstance.id}
            selected={selected}
            onAddUser={handleAddUser}
            onRemoveUser={handleRemoveUser} />
        </Box>
      </DialogContent>

      <Footer>
        <SecondaryButton
          onClick={handleCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          disabled={!enables.send}
          onClick={handleSend}>
          Send
        </PrimaryButton>
      </Footer>

    </Dialog>
  );
};

const Title = styled(DialogTitle)(({ theme }) => ({
  marginBottom: 10,
  fontFamily: theme.fonts.bold,
  fontSize: 22,
  textAlign: 'center',
  color: theme.palette.text.primary,
}));

const Footer = styled(DialogActions)(({
  display: 'flex',
  justifyContent: 'center',
}));

export const useShareChatModal = () => useModal(ShareChatModal);